import type { AxiosError } from "axios";
import { useRouter } from "next/router";
import { useCallback } from "react";
import { toast } from "react-toastify";
import useSWR from "swr";
import type { NskSwrErrorResponse, SwrReturnType } from "~/models/Api";
import type { Deal, DealIndexVendor, DealSearchCondition } from "~/models/Deal";
import { convertQueryStringUrl } from "~/utils/DealSearchConditionUtils";
import { generatePath } from "~/utils/generatePath";

const paths = {
	index: "/vendors/deals",
	show: "/vendors/deals/:id"
} as const;
type TIndex = (
	query: DealSearchCondition
) => SwrReturnType<Pageable<DealIndexVendor>>;
type TShow = (id: number | null) => SwrReturnType<Deal>;
type ReturnType = {
	index: TIndex;
	show: TShow;
};

export const dealCacheKey = (id: number): string =>
	generatePath("/vendors/deals/:id", { id });

export const useDeals = (): ReturnType => {
	const router = useRouter();

	const index: TIndex = (query: DealSearchCondition) => {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { data, mutate, error } = useSWR<Pageable<DealIndexVendor>>(
			convertQueryStringUrl(paths.index, query)
		);
		return {
			data: data ?? null,
			error,
			isLoading: !data && !error,
			mutate
		};
	};

	const handleDealShowError = useCallback(
		(error: AxiosError<NskSwrErrorResponse>) => {
			if (error.response?.status === 404) {
				toast.error("この案件にアクセスする権限がないか、案件が存在しません。");
				void router.replace(generatePath(paths.index));
			}
		},
		[router]
	);

	const show: TShow = (id: number | null) => {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { data, mutate, error } = useSWR<Deal>(
			id === null ? null : paths.show.replace(":id", String(id)),
			{ onError: handleDealShowError }
		);
		return {
			data: data ?? null,
			error,
			isLoading: !data && !error,
			mutate
		};
	};
	return { index, show };
};
